import { AUTH_STORAGE_KEY } from "../constants/auth.constants";

const UUID_TOKEN_REGEX =
  /^(idp-)?[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const TOKEN_KEY_REGEX =
  /^(accessToken|access_token|token|authToken|authorizationToken|authorization|sessionToken|sessionId|refreshToken|refresh_token|idToken|id_token|jwt|headerToken|x-auth-token|xAuthToken)$/i;

const WRAPPER_KEY_REGEX =
  /^(data|result|payload|user|auth|authentication|session|tokenInfo|credentials|tokens)$/i;

export const stripBearer = (value) =>
  String(value || "")
    .replace(/^Bearer\s+/i, "")
    .trim();

const isUuidToken = (value) => UUID_TOKEN_REGEX.test(stripBearer(value));

const collectTokenStrings = (value, into = [], depth = 0) => {
  if (value == null || depth > 6) return into;
  if (typeof value === "string") {
    const cleaned = stripBearer(value);
    if (cleaned) into.push(cleaned);
    return into;
  }
  if (Array.isArray(value) || typeof value !== "object") return into;

  Object.entries(value).forEach(([key, nested]) => {
    if (TOKEN_KEY_REGEX.test(key)) {
      collectTokenStrings(nested, into, depth + 1);
      return;
    }
    if (WRAPPER_KEY_REGEX.test(key) && nested && typeof nested === "object") {
      collectTokenStrings(nested, into, depth + 1);
    }
  });
  return into;
};

const tokenCandidates = (source = {}) => {
  const refresh = stripBearer(
    source.refreshToken || source.refresh_token || source.user?.refreshToken || ""
  );
  return [
    source.accessToken,
    source.access_token,
    source.token,
    source.authToken,
    source.authorizationToken,
    source.sessionToken,
    source.sessionId,
    source.idToken,
    source.id_token,
    source.jwt,
    source.user?.accessToken,
    source.user?.access_token,
    source.user?.token,
    source.user?.authToken,
    source.user?.sessionToken,
  ]
    .map(stripBearer)
    .filter(Boolean)
    .filter((value) => value !== refresh);
};

export const pickAccessToken = (source = {}) => {
  const candidates = tokenCandidates(source);
  return candidates.find(isUuidToken) || candidates[0] || null;
};

export const pickTenantApiToken = (source = {}) => {
  const candidates = [...new Set(collectTokenStrings(source, []))];
  return candidates.find(isUuidToken) || candidates[0] || null;
};

export const parseJwtPayload = (token) => {
  try {
    if (!token || typeof token !== "string") return {};
    const payload = token.split(".")[1];
    if (!payload) return {};
    const base64 = payload.replace(/-/g, "+").replace(/_/g, "/");
    const decoded = atob(base64);
    return JSON.parse(decoded);
  } catch {
    return {};
  }
};

export const pickUserId = (source = {}) => {
  const tokenPayload = parseJwtPayload(pickAccessToken(source) || "");
  return (
    source.userId ||
    source.id ||
    source._id ||
    source.sub ||
    source.user?.userId ||
    source.user?.id ||
    source.user?._id ||
    tokenPayload.userId ||
    tokenPayload.id ||
    tokenPayload.sub ||
    ""
  );
};

export const pickRefreshToken = (source = {}) =>
  source.refreshToken ||
  source.refresh_token ||
  source.user?.refreshToken ||
  source.user?.refresh_token ||
  null;

export const saveAuthSession = (auth = {}) => {
  try {
    sessionStorage.setItem(
      AUTH_STORAGE_KEY,
      JSON.stringify({
        loggedIn: true,
        user: auth.user || {},
        userType: auth.userType || null,
        tenantId: auth.tenantId || null,
        activeTenantId: auth.activeTenantId || null,
        activeTenantName: auth.activeTenantName || null,
        roles: auth.roles || [],
        permissions: auth.permissions || [],
      })
    );
  } catch {
    // ignore storage failures
  }
};

export const loadAuthSession = () => {
  try {
    const raw = sessionStorage.getItem(AUTH_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.loggedIn) return null;
    return parsed;
  } catch {
    return null;
  }
};

export const clearAuthSession = () => {
  try {
    sessionStorage.removeItem(AUTH_STORAGE_KEY);
  } catch {
    // ignore storage failures
  }
};
